import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../../App";

const cache = {};

export function useDropdown(key) {
  const [values, setValues] = useState(cache[key] || []);
  const [loading, setLoading] = useState(!cache[key]);

  useEffect(() => {
    if (cache[key]) { setValues(cache[key]); setLoading(false); return; }
    let cancelled = false;
    axios.get(`${API}/customization/dropdowns/${key}`, { headers: getAuthHeaders() })
      .then(resp => {
        if (!cancelled) {
          const v = resp.data.values || [];
          cache[key] = v;
          setValues(v);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [key]);

  return { values, loading };
}

export function useMultipleDropdowns(keys) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const uncached = keys.filter(k => !cache[k]);
    if (uncached.length === 0) {
      const result = {};
      keys.forEach(k => { result[k] = cache[k]; });
      setData(result);
      setLoading(false);
      return;
    }
    Promise.allSettled(
      uncached.map(k => axios.get(`${API}/customization/dropdowns/${k}`, { headers: getAuthHeaders() }).then(r => ({ key: k, values: r.data.values || [] })))
    ).then(results => {
      results.forEach(r => {
        if (r.status === "fulfilled") cache[r.value.key] = r.value.values;
      });
      const result = {};
      keys.forEach(k => { result[k] = cache[k] || []; });
      setData(result);
      setLoading(false);
    });
  }, [keys.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading };
}

// Helper to render a select from dropdown values
export function DropdownSelect({ dropdownKey, value, onChange, className, testId, placeholder }) {
  const { values, loading } = useDropdown(dropdownKey);
  if (loading) return <select className={className} disabled><option>Loading...</option></select>;
  return (
    <select value={value || ""} onChange={onChange} className={className} data-testid={testId}>
      {placeholder && <option value="">{placeholder}</option>}
      {values.map(v => (
        <option key={v.value} value={v.value}>{v.label}</option>
      ))}
    </select>
  );
}

// Helper to get label + color for a value
export function useDropdownLabel(key, value) {
  const { values } = useDropdown(key);
  const match = values.find(v => v.value === value);
  return { label: match?.label || value || "", color: match?.color || "#64748B" };
}

// Status badge that uses dropdown colors
export function DropdownBadge({ dropdownKey, value, className = "" }) {
  const { label, color } = useDropdownLabel(dropdownKey, value);
  return (
    <span
      className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider border ${className}`}
      style={{ borderColor: color, color: color, backgroundColor: `${color}15` }}
      data-testid={`badge-${dropdownKey}-${value}`}
    >
      {label}
    </span>
  );
}

// Clear cache (call after saving dropdown changes)
export function clearDropdownCache(key) {
  if (key) delete cache[key];
  else Object.keys(cache).forEach(k => delete cache[k]);
}
